/**
 * `/docs/agent` — the AI-agent on-ramp.
 *
 * The fastest, least-technical way in: the user copies one prompt into
 * their coding agent (Claude Code, Cursor, Claude Desktop, …). The
 * agent installs the Clickweave skill from the public `skill.md`, which
 * then walks it through installing the `clickweave` CLI, `clickweave login`, and first-
 * run setup — so everything after works in plain natural language.
 *
 * Keep this page free of raw API-key / cURL content; that lives in
 * `/docs/quickstart`. This page is the pixel page's sibling: a
 * no-deep-technical-knowledge starting point.
 */
import { CodeSnippet } from '@/components/primitives/CodeSnippet';
import { CopyPrompt } from '@/components/primitives/CopyPrompt';
import { Island } from '@/components/shell/Island';
import { Button } from '@/components/ui/button';
import { AGENT_SETUP_PROMPT, CLI_PACKAGE, SKILL_URL } from '@/lib/agent-onboarding';
import { Broadcast, Code, Terminal } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/docs/agent')({
  component: AgentDocs,
});

const MCP_CONFIG =
  '{\n' +
  '  "mcpServers": {\n' +
  '    "clickweave": {\n' +
  '      "command": "clickweave",\n' +
  '      "args": ["mcp"]\n' +
  '    }\n' +
  '  }\n' +
  '}';

function AgentDocs(): ReactElement {
  return (
    <Island breadcrumb={[{ label: 'Docs', href: '/docs' }, { label: 'AI agent setup' }]}>
      <div className="mx-auto max-w-3xl space-y-10">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold text-[var(--color-ink)]">
            Set up with your AI agent
          </h1>
          <p className="text-sm text-[var(--color-mute)]">
            Point your AI coding agent — Claude Code, Cursor, Claude Desktop, or any tool that runs
            commands — at Clickweave and it does the setup for you: installs the CLI, signs you in,
            turns on analytics, and runs your first performance audit. No API keys to juggle, no
            docs to read first.
          </p>
        </header>

        <section className="space-y-3">
          <p className="text-sm text-[var(--color-mute)]">
            Copy this prompt and paste it into your agent:
          </p>
          <CopyPrompt prompt={AGENT_SETUP_PROMPT} />
          <p className="text-xs text-[var(--color-mute)]">
            It fetches the Clickweave skill from{' '}
            <a href={SKILL_URL} className="underline" target="_blank" rel="noreferrer">
              {SKILL_URL.replace('https://', '')}
            </a>{' '}
            and follows it. You'll approve the browser sign-in when{' '}
            <span className="font-mono">clickweave login</span> opens it.
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-base font-semibold text-[var(--color-ink)]">What it does</h2>
          <Step
            number={1}
            title="Installs the Clickweave skill"
            body="The agent fetches skill.md and saves it as a skill, so it knows every Clickweave command and the right order to run them in."
          />
          <Step
            number={2}
            title="Installs the CLI and signs you in"
            body={
              <>
                It runs <span className="font-mono text-xs">npm i -g {CLI_PACKAGE}</span> then{' '}
                <span className="font-mono text-xs">clickweave login</span>, which opens your
                browser to approve. A scoped key is stored locally — the CLI then acts as you, with
                your org role deciding what it can do.
              </>
            }
          />
          <Step
            number={3}
            title="Sets you up, then takes instructions"
            body={
              <>
                It turns on analytics (the pixel) and runs a first audit. From then on you just ask
                — "how's LCP on the pricing page?", "did my last deploy regress anything?" — and it
                maps that to the right <span className="font-mono text-xs">clickweave</span>{' '}
                commands.
              </>
            }
          />
        </section>

        <section className="space-y-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
          <div className="flex items-center gap-2">
            <Terminal
              aria-hidden="true"
              size={20}
              weight="light"
              className="text-[var(--color-mute)]/80"
            />
            <h2 className="text-base font-semibold text-[var(--color-ink)]">
              Install the skill yourself
            </h2>
          </div>
          <p className="text-sm text-[var(--color-mute)]">
            Prefer to add it by hand? Save the skill into your agent's skills directory. For Claude
            Code that's{' '}
            <span className="font-mono text-xs">.claude/skills/clickweave/SKILL.md</span>:
          </p>
          <CodeSnippet
            code={`mkdir -p .claude/skills/clickweave\ncurl -sL ${SKILL_URL} -o .claude/skills/clickweave/SKILL.md`}
          />
        </section>

        <section className="space-y-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
          <div className="flex items-center gap-2">
            <Code
              aria-hidden="true"
              size={20}
              weight="light"
              className="text-[var(--color-mute)]/80"
            />
            <h2 className="text-base font-semibold text-[var(--color-ink)]">
              Direct tool access (MCP)
            </h2>
          </div>
          <p className="text-sm text-[var(--color-mute)]">
            Power users can give the agent Clickweave's tools directly instead of shelling out to
            the CLI. The CLI ships a local MCP server — add it to your host's config (Claude
            Desktop, Cursor, Claude Code). Sign in once with{' '}
            <span className="font-mono text-xs">clickweave login</span> first; the server reuses
            that credential.
          </p>
          <CodeSnippet code={MCP_CONFIG} title="mcp config" />
        </section>

        <footer className="flex flex-wrap gap-3 border-t border-[var(--color-line-soft)] pt-6">
          <Button size="sm" variant="outline" render={<Link to="/docs/pixel" />}>
            <Broadcast aria-hidden="true" />
            Just want the numbers? Install the pixel
          </Button>
          <Button size="sm" variant="ghost" render={<Link to="/docs/quickstart" />}>
            <Code aria-hidden="true" />
            Building an integration? API quickstart
          </Button>
        </footer>
      </div>
    </Island>
  );
}

function Step({
  number,
  title,
  body,
}: {
  number: number;
  title: string;
  body: ReactElement | string;
}): ReactElement {
  return (
    <div className="flex gap-3">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-ink)] text-xs font-semibold text-[var(--color-paper)]">
        {number}
      </span>
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-[var(--color-ink)]">{title}</h3>
        <p className="text-sm text-[var(--color-mute)]">{body}</p>
      </div>
    </div>
  );
}
